/* ── Global Search — Supabase helpers ─────────────────────────────────────
   Pure data-fetching functions, no React here, so they're easy to unit test
   and reuse (e.g. from an admin search box later) independent of the UI.

   Design notes:
   - Every query destructures { data, error } and handles its own error —
     no Promise.all silently swallowing a failed leg.
   - Team/player results are capped (8–10 rows) since this renders in a
     dropdown, not a results page.
   - Matchup ("X vs Y") search resolves team name(s) → team id(s) first,
     then queries games directly by id, rather than trying to filter on
     embedded/joined columns — simpler to reason about and avoids
     PostgREST embedded-filter edge cases with OR logic across two aliases.
──────────────────────────────────────────────────────────────────────── */
import { supabase } from "../supabaseClient";

const MIN_QUERY_LENGTH = 2;
// Matches "vs" or "vs." as a whole word, case-insensitive — so "Vs" and
// "VS" work, but a team literally named e.g. "Vsomething" doesn't false-split.
const VS_PATTERN = /\bvs\.?\b/i;

/** True if the query looks like a matchup search ("Team A vs Team B"). */
export function isMatchupQuery(query) {
  return VS_PATTERN.test(query);
}

/** Splits "Team A vs Team B" (or "Team A vs") into trimmed sides. */
export function parseMatchupQuery(query) {
  const [rawA = "", rawB = ""] = query.split(VS_PATTERN);
  return { sideA: rawA.trim(), sideB: rawB.trim() };
}

/**
 * Groups flat team rows (one per season) by team name, so the UI can show
 * one card per franchise with each season as a selectable sub-entry.
 * Each entry keeps its own `id` — that's the season-specific team id used
 * for routing to /teams/:id.
 */
export function groupTeamsByName(rows) {
  const map = new Map();

  rows.forEach((row) => {
    if (!map.has(row.name)) {
      map.set(row.name, { name: row.name, logoUrl: row.logo_url, entries: [] });
    }
    map.get(row.name).entries.push({
      id: row.id,
      seasonId: row.season_id,
      seasonName: row.seasons?.name ?? "Unknown season",
      wins: row.wins ?? 0,
      losses: row.losses ?? 0,
    });
  });

  const groups = Array.from(map.values());
  // Most recent season first within each team's group.
  groups.forEach((g) => g.entries.sort((a, b) => b.seasonId - a.seasonId));
  return groups.slice(0, 8);
}

/** Teams matching `query` by name, across all seasons, grouped by name. */
export async function searchTeams(query) {
  const { data, error } = await supabase
    .from("teams")
    .select("id, name, logo_url, wins, losses, season_id, seasons(name)")
    .ilike("name", `%${query}%`)
    .order("season_id", { ascending: false })
    .limit(20); // fetched pre-grouping; groupTeamsByName caps the final list

  if (error) {
    console.error("searchTeams failed:", error);
    return [];
  }
  return groupTeamsByName(data || []);
}

/**
 * Players matching `query` by name.
 * Only players with a linked `profile_id` are returned — players who
 * haven't created/claimed an account yet have no /players/:profileId page
 * to route to, so surfacing them here would be a dead link.
 */
export async function searchPlayers(query) {
  const { data, error } = await supabase
    .from("players")
    .select(
      "id, name, jersey_number, profile_id, team_id, season_id, teams(name), seasons(name)",
    )
    .ilike("name", `%${query}%`)
    .not("profile_id", "is", null)
    .order("season_id", { ascending: false })
    .limit(8);

  if (error) {
    console.error("searchPlayers failed:", error);
    return [];
  }
  return data || [];
}

/** Resolves a free-text team name fragment to matching team ids (any season). */
async function findTeamIds(nameFragment) {
  if (!nameFragment) return [];
  const { data, error } = await supabase
    .from("teams")
    .select("id")
    .ilike("name", `%${nameFragment}%`)
    .limit(25);

  if (error) {
    console.error("findTeamIds failed:", error);
    return [];
  }
  return (data || []).map((t) => t.id);
}

/**
 * "Vs" matchup search. Handles both a complete pair ("A vs B") and a
 * dangling side ("A vs") — the latter just returns A's games, either side.
 */
export async function searchGamesByMatchup(sideA, sideB) {
  const idsA = await findTeamIds(sideA);
  if (idsA.length === 0) return [];

  let orFilter;
  if (sideB) {
    const idsB = await findTeamIds(sideB);
    if (idsB.length === 0) return [];
    const a = idsA.join(",");
    const b = idsB.join(",");
    // Either ordering counts as the same matchup: A@B or B@A.
    orFilter = `and(team1_id.in.(${a}),team2_id.in.(${b})),and(team1_id.in.(${b}),team2_id.in.(${a}))`;
  } else {
    const a = idsA.join(",");
    orFilter = `team1_id.in.(${a}),team2_id.in.(${a})`;
  }

  const { data, error } = await supabase
    .from("games")
    .select(
      `id, game_date, status, team1_score, team2_score, season_id,
       seasons(name),
       team1:teams!games_team1_id_fkey(name, logo_url),
       team2:teams!games_team2_id_fkey(name, logo_url)`,
    )
    .or(orFilter)
    .order("game_date", { ascending: false })
    .limit(10);

  if (error) {
    console.error("searchGamesByMatchup failed:", error);
    return [];
  }
  return data || [];
}

/**
 * Single entry point the search bar calls. Decides matchup-mode vs.
 * normal-mode and fans out to the right helpers.
 *   - "Team A vs Team B" / "Team A vs" → games only (matchup logic)
 *   - anything else                    → teams + players
 */
export async function runGlobalSearch(rawQuery) {
  const query = rawQuery.trim();
  if (query.length < MIN_QUERY_LENGTH) {
    return { teams: [], players: [], games: [] };
  }

  if (isMatchupQuery(query)) {
    const { sideA, sideB } = parseMatchupQuery(query);
    if (!sideA) return { teams: [], players: [], games: [] };
    const games = await searchGamesByMatchup(sideA, sideB);
    return { teams: [], players: [], games };
  }

  const [teams, players] = await Promise.all([
    searchTeams(query),
    searchPlayers(query),
  ]);
  return { teams, players, games: [] };
}
